/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import React, { useState } from "react";

import {
  Alert,
  Checkbox,
  Col,
  DatePicker,
  InputNumber,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import moment from "moment";
import PropTypes from "prop-types";

import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";

import { useAppTheme } from "../../../context/ThemeProvider";
import {
  // DATE_FORMAT,
  TIER_COLOR_MAP,
  WL_PLAN_COLOR_MAP,
} from "../../../Utils/constants";
import { numFormatter } from "../../../Utils/formatter";
import {
  deepCopyFeatureConfig,
  ensureDefaultAllowedUsers,
  FEATURE_INCLUDED_TIERS,
  FEATURE_LABELS,
  FEATURE_MODAL_TOOLTIP,
  AI_VERSE_BUNDLE_FEATURES,
  COLUMN_FEATURES,
  formatAiCredits,
  getAiVerseBundleChanges,
  getAllInitialFeaturesConfig,
  getDefaultExpiryAt,
  getInitialFeatureConfig,
  getFeatureColumnLabel,
  getFeatureColumnWidth,
  getMangoAiMonthlyAvailable,
  hasMangoAiCreditTopUp,
  isDefaultHost,
  isExpiryMandatoryForFeature,
  isExpiryRequiredForFeature,
  isFeatureHostRestricted,
  isInstantWithdrawalActive,
  isWithdrawalDelayEditable,
  MANAGEABLE_FEATURES,
  MANGO_AI_CREDIT_FIELD,
  MANGO_AI_INDEPENDENT_FEATURES,
  PLAN_TYPE_OPTIONS,
  resolveAiVerseAfterChange,
  syncWithdrawalDelayWithInstantWithdrawal,
  USER_TIER_OPTIONS,
} from "../manageFeaturesUtils";

const UsersTable = ({
  data,
  onDataChange,
  isSameForAll,
  onSameForAllChange,
  isPreloaded = false,
}) => {
  const { COLORS } = useAppTheme();
  const [sameForAllByFeature, setSameForAllByFeature] = useState({});

  // Reset states when data changes (new CSV upload)
  React.useEffect(() => {
    setSameForAllByFeature({});
  }, [data.length]);

  // Clear feature-level same for all when global is enabled
  React.useEffect(() => {
    if (isSameForAll) {
      setSameForAllByFeature({});
    }
  }, [isSameForAll]);

  // REMOVED: Bulk apply function that was causing cross-column contamination
  // The "Enable all" and "Expires on" controls should not affect all features
  // Each feature should be managed independently (column-wise)

  const updateUserFeature = (newData, dataIndex, feature, field, value) => {
    const user = newData[dataIndex];
    const featuresConfig = user.featuresConfig
      ? { ...user.featuresConfig }
      : getAllInitialFeaturesConfig();
    const featureConfig = featuresConfig[feature]
      ? { ...featuresConfig[feature] }
      : getInitialFeatureConfig(feature);

    featureConfig[field] = value;

    if (field === "enabled") {
      const originalConfig = user._originalFeaturesConfig?.[feature];

      if (value) {
        if (
          featureConfig.expiryAt &&
          moment(featureConfig.expiryAt).isBefore(moment())
        ) {
          featureConfig.expiryAt = null;
        }
      } else if (originalConfig?.enabled) {
        featureConfig.expiryAt = null;
      } else {
        featureConfig.expiryAt = originalConfig?.expiryAt || null;
      }

      // a feature whose expiry is mandatory is never switched on without one:
      // the stored date is kept when it is still usable (the branches above
      // clear it when it has already passed), otherwise it defaults to a year
      if (
        value &&
        isExpiryMandatoryForFeature(feature) &&
        !featureConfig.expiryAt
      ) {
        featureConfig.expiryAt = getDefaultExpiryAt();
      }
    }

    // Ensure the feature config is valid
    featuresConfig[feature] = ensureDefaultAllowedUsers(featureConfig, feature);

    // Update the user data
    newData[dataIndex] = { ...user, featuresConfig };
  };

  const handleFeatureChange = (userIndex, feature, field, value) => {
    const newData = [...data];
    const validUsers = newData.filter((user) => user.status === "valid");
    const currentUser = validUsers[userIndex];
    const actualDataIndex = newData.findIndex(
      (user) => user.key === currentUser.key
    );

    // STEP 1: Always apply the change to the current user
    updateUserFeature(newData, actualDataIndex, feature, field, value);

    // STEP 2: Handle propagation - ONLY first row can propagate
    // const isFirstRow = userIndex === 0;
    const isFirstRow = true; // Intentionally marking it as true for now 16th April 2026 TMG - 1707

    if (isFirstRow) {
      const shouldPropagate = isSameForAll || sameForAllByFeature[feature];

      if (shouldPropagate) {
        applyToAllUsers(
          newData,
          validUsers,
          actualDataIndex,
          feature,
          field,
          value
        );
      }
    } else {
      if (isSameForAll) {
        onSameForAllChange(false);
      }
      if (sameForAllByFeature[feature]) {
        setSameForAllByFeature((prev) => ({ ...prev, [feature]: false }));
      }
    }

    // AI Verse moves in lockstep with the four features it bundles, so granting
    // or revoking one of them here (or via propagation) can move the add-on.
    // Only `enabled` counts: an expiry edit leaves the parent alone.
    if (field === "enabled") {
      newData.forEach((user, index) => {
        if (user.status !== "valid") return;

        const nextAiVerse = resolveAiVerseAfterChange(
          user.aiVerse,
          user.featuresConfig,
          [feature]
        );
        if (nextAiVerse !== user.aiVerse) {
          newData[index] = { ...user, aiVerse: nextAiVerse };
        }
      });
    }

    // toggling Instant Withdrawal off (here or via propagation) must drop any
    // pending delay edit on the affected rows
    onDataChange(syncWithdrawalDelayWithInstantWithdrawal(newData));
  };

  const applyToAllUsers = (
    newData,
    validUsers,
    currentUserIndex,
    feature,
    field,
    value
  ) => {
    validUsers.forEach((user) => {
      const targetIndex = newData.findIndex((u) => u.key === user.key);
      if (targetIndex === currentUserIndex) return;
      if (isFeatureHostRestricted(feature, user.host)) return;

      updateUserFeature(newData, targetIndex, feature, field, value);
    });
  };

  const handleSameForAllFeature = (feature, checked) => {
    setSameForAllByFeature((prev) => ({ ...prev, [feature]: checked }));

    if (checked) {
      const validUsers = data.filter((user) => user.status === "valid");
      if (validUsers.length > 0 && validUsers[0].featuresConfig?.[feature]) {
        const firstUserConfig = validUsers[0].featuresConfig[feature];
        const newData = [...data];

        validUsers.slice(1).forEach((user) => {
          const targetIndex = newData.findIndex((u) => u.key === user.key);
          if (targetIndex === -1) return;
          if (isFeatureHostRestricted(feature, user.host)) return;

          if (!newData[targetIndex].featuresConfig) {
            newData[targetIndex].featuresConfig = getAllInitialFeaturesConfig();
          }
          newData[targetIndex].featuresConfig[feature] =
            deepCopyFeatureConfig(firstUserConfig);
          newData[targetIndex].featuresConfig[feature] =
            ensureDefaultAllowedUsers(
              newData[targetIndex].featuresConfig[feature],
              feature
            );

          // AI Verse is derived from the four features it bundles, so copying
          // one of them can move the parent on the target row. Recompute from
          // that row's own config rather than copying the source's flag:
          // copying Mango AI alone onto a row whose Theme/Banner/Website
          // features are off would otherwise leave aiVerse true over an
          // incomplete bundle. A feature outside the bundle leaves it alone.
          // _originalAiVerse is untouched on purpose: it is this row's own
          // stored state, and it decides whether `aiVerse` is sent at all.
          newData[targetIndex] = {
            ...newData[targetIndex],
            aiVerse: resolveAiVerseAfterChange(
              newData[targetIndex].aiVerse,
              newData[targetIndex].featuresConfig,
              [feature]
            ),
          };
        });

        onDataChange(syncWithdrawalDelayWithInstantWithdrawal(newData));
      }
    }
  };

  const isFeatureIncludedInTier = (feature, userTier) => {
    const includedTiers = FEATURE_INCLUDED_TIERS[feature];
    return includedTiers && userTier && includedTiers.includes(userTier);
  };

  // The standard-withdrawal delay lives on the host's CustomHost, not in
  // featuresConfig, so it has its own change handler outside updateUserFeature.
  // Keyed by the rendered record, not by row index — the table index counts
  // invalid rows, so it cannot be used against the filtered valid-users list.
  const handleWithdrawalDelayChange = (recordKey, value) => {
    const newData = [...data];
    const actualDataIndex = newData.findIndex((user) => user.key === recordKey);
    if (actualDataIndex === -1) return;

    newData[actualDataIndex] = {
      ...newData[actualDataIndex],
      standardWithdrawalDelay: value,
    };

    if (isSameForAll) {
      newData.forEach((user, targetIndex) => {
        if (targetIndex === actualDataIndex) return;
        if (user.status !== "valid") return;
        // skip default TagMango hosts and hosts without Instant Withdrawal —
        // neither has a configurable withdrawal delay
        if (!isWithdrawalDelayEditable(user)) return;

        newData[targetIndex] = {
          ...newData[targetIndex],
          standardWithdrawalDelay: value,
        };
      });
    }

    onDataChange(newData);
  };

  // AI Verse is the parent of four AI features, but it is an add-on stored in
  // addOnsConfig rather than part of featuresConfig. One click therefore has to
  // write the row-level flag and several features together, on a single copy of
  // the data: calling handleFeatureChange per feature and a separate add-on
  // handler would not work, because each starts from the same stale `data` and
  // the last would discard the others' writes.
  const handleAiVerseChange = (recordKey, checked) => {
    const newData = [...data];
    const sourceIndex = newData.findIndex((user) => user.key === recordKey);
    if (sourceIndex === -1) return;

    const bundleChanges = getAiVerseBundleChanges(checked);

    newData.forEach((user, index) => {
      if (user.status !== "valid") return;
      if (index !== sourceIndex && !isSameForAll) return;

      Object.entries(bundleChanges).forEach(([feature, enabled]) => {
        if (isFeatureHostRestricted(feature, newData[index].host)) return;
        updateUserFeature(newData, index, feature, "enabled", enabled);
      });

      newData[index] = { ...newData[index], aiVerse: checked };
    });

    onDataChange(syncWithdrawalDelayWithInstantWithdrawal(newData));
  };

  const renderMangoAiCredits = (record, userIndex) => {
    const feature = MANAGEABLE_FEATURES.MANGO_AI;
    const featureConfig =
      record.featuresConfig?.[feature] || getInitialFeatureConfig(feature);
    const storedConfig = record._originalFeaturesConfig?.[feature];
    const storedCredits = storedConfig?.[MANGO_AI_CREDIT_FIELD];
    const monthlyAvailable = getMangoAiMonthlyAvailable(storedConfig);
    const topUp = featureConfig[MANGO_AI_CREDIT_FIELD];

    return (
      <div>
        <Typography.Text
          style={{
            fontSize: 11,
            color: COLORS.TEXT_SUBTITLE,
            display: "block",
          }}
        >
          <strong style={{ fontWeight: 500 }}>Balance: </strong>
          {formatAiCredits(storedCredits) ?? "—"}
        </Typography.Text>

        {monthlyAvailable !== null && (
          <Typography.Text
            style={{
              fontSize: 11,
              color: COLORS.TEXT_SUBTITLE,
              display: "block",
            }}
          >
            <strong style={{ fontWeight: 500 }}>Monthly available: </strong>
            {formatAiCredits(monthlyAvailable)}
          </Typography.Text>
        )}

        <InputNumber
          size="small"
          value={topUp}
          onChange={(value) =>
            handleFeatureChange(
              userIndex,
              feature,
              MANGO_AI_CREDIT_FIELD,
              value
            )
          }
          placeholder="Add credits"
          min={0}
          precision={0}
          style={{ width: "100%", marginTop: 4 }}
        />

        {hasMangoAiCreditTopUp(featureConfig) && (
          <Typography.Text
            style={{
              fontSize: 10,
              color: COLORS.TEXT_SUBTITLE,
              display: "block",
              marginTop: 2,
            }}
          >
            {typeof storedCredits === "number"
              ? `Adds ${topUp} → ${formatAiCredits(storedCredits + topUp)}`
              : `Adds ${topUp}`}
          </Typography.Text>
        )}
      </div>
    );
  };

  // How long a grant has left, shown under its date picker. Shared by the AI
  // feature rows and the generic feature columns so both read the same way.
  const renderExpiryCountdown = (expiryAt) => {
    if (!expiryAt) return null;

    const expiryMoment = moment(expiryAt);
    const now = moment();

    let expiryText;
    let expiryColor = COLORS.TEXT_SUBTITLE;

    if (expiryMoment.isBefore(now)) {
      expiryColor = COLORS.ERROR;
      expiryText = `Expired ${expiryMoment.fromNow()}`;
    } else {
      const daysLeft = expiryMoment.diff(now, "days");
      const hoursLeft = expiryMoment.diff(now, "hours");

      if (daysLeft >= 1) {
        expiryText = `Expires in ${daysLeft} ${
          daysLeft === 1 ? "day" : "days"
        }`;
      } else if (hoursLeft >= 1) {
        expiryText = `Expires in ${hoursLeft} ${
          hoursLeft === 1 ? "hour" : "hours"
        }`;
      } else {
        expiryText = "Expires soon";
      }
    }

    return (
      <Typography.Text
        style={{
          fontSize: 10,
          color: expiryColor,
          display: "block",
          marginTop: 2,
        }}
      >
        <ClockCircleOutlined style={{ marginRight: 2 }} />
        {expiryText}
      </Typography.Text>
    );
  };

  // One row of the Mango AI group: the switch, and its own expiry picker
  // directly beneath it once the feature is on. Each row is now a feature of
  // its own, so the expiry belongs to that feature rather than to the group.
  const renderMangoAiFeatureRow = (record, feature, userIndex) => {
    const featureConfig =
      record.featuresConfig?.[feature] || getInitialFeatureConfig(feature);
    const expiryRequired =
      featureConfig.enabled && isExpiryMandatoryForFeature(feature);
    const expiryMissing = expiryRequired && !featureConfig.expiryAt;

    return (
      <div key={feature}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <Typography.Text
            style={{
              fontSize: 12,
              whiteSpace: "nowrap",
              color: COLORS.TEXT_BODY,
            }}
          >
            {FEATURE_LABELS[feature]}
          </Typography.Text>
          <Switch
            size="small"
            checked={!!featureConfig.enabled}
            onChange={(checked) =>
              handleFeatureChange(userIndex, feature, "enabled", checked)
            }
          />
        </div>

        {featureConfig.enabled && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginTop: 4,
              }}
            >
              <DatePicker
                size="small"
                value={
                  featureConfig.expiryAt ? moment(featureConfig.expiryAt) : null
                }
                onChange={(date) =>
                  handleFeatureChange(
                    userIndex,
                    feature,
                    "expiryAt",
                    date?.endOf("day")?.toISOString() || null
                  )
                }
                disabledDate={(current) =>
                  current && current < moment().startOf("day")
                }
                placeholder={expiryRequired ? "Select expiry" : "No expiry"}
                status={expiryMissing ? "error" : undefined}
                style={{
                  flex: 1,
                  minWidth: 0,
                  ...(expiryMissing ? { borderColor: COLORS.ERROR } : {}),
                }}
              />
              {expiryRequired && (
                <span style={{ color: COLORS.ERROR, lineHeight: 1 }}>*</span>
              )}
            </div>
            {renderExpiryCountdown(featureConfig.expiryAt)}

            {expiryMissing && (
              <Typography.Text
                style={{
                  fontSize: 10,
                  color: COLORS.ERROR,
                  display: "block",
                  marginTop: 2,
                }}
              >
                Expiry date is required for this feature
              </Typography.Text>
            )}
          </>
        )}
      </div>
    );
  };

  // The seven AI features share one cell, with AI Verse as its parent: the four
  // it bundles above a divider, the three independent of it below, and the
  // credit balance at the bottom. The box is a TOP-UP amount — whatever is in
  // it is added to the balance on save — so the stored balance is shown as
  // read-only text beside it and never as an editable value.
  const renderMangoAiGroup = (record, userIndex) => {
    return (
      <Space direction="vertical" size={6} style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <Typography.Text
            style={{
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: COLORS.TEXT_TITLE,
            }}
          >
            AI Verse
            <Tooltip title="Turning this on only activates the AI Verse card on the creator's plans page. It moves in lockstep with the four features above the divider: on grants all four, off revokes all four, and it stays on only while all four are on.">
              <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 12 }} />
            </Tooltip>
          </Typography.Text>
          <Switch
            size="small"
            checked={record.aiVerse === true}
            onChange={(checked) => handleAiVerseChange(record.key, checked)}
          />
        </div>

        <div
          style={{
            paddingLeft: 8,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {AI_VERSE_BUNDLE_FEATURES.map((bundled) =>
            renderMangoAiFeatureRow(record, bundled, userIndex)
          )}

          {/* below the divider: AI features AI Verse never moves */}
          <div
            style={{
              borderTop: `1px solid ${COLORS.BORDER}`,
              paddingTop: 6,
              marginTop: 2,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {MANGO_AI_INDEPENDENT_FEATURES.map((independent) =>
              renderMangoAiFeatureRow(record, independent, userIndex)
            )}
          </div>
        </div>
      </Space>
    );
  };

  const renderFeatureControl = (record, feature, userIndex) => {
    const featureConfig =
      record.featuresConfig?.[feature] || getInitialFeatureConfig(feature);
    const showTierWarning = isFeatureIncludedInTier(feature, record.userTier);
    const isHostRestricted = isFeatureHostRestricted(feature, record.host);
    const expiryRequired =
      featureConfig.enabled &&
      isExpiryRequiredForFeature(feature, record.userTier);
    const expiryMissing = expiryRequired && !featureConfig.expiryAt;

    if (feature === MANAGEABLE_FEATURES.MANGO_AI) {
      return renderMangoAiGroup(record, userIndex);
    }

    return (
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        {feature === MANAGEABLE_FEATURES.STORAGE_LIMIT ? (
          <div>
            {record.userTier &&
            ["tier2", "tier3", "tier4"].includes(record.userTier) ? (
              <Typography.Text
                type="success"
                style={{
                  fontSize: 14,
                  color: COLORS.SUCCESS,
                }}
              >
                Unlimited
              </Typography.Text>
            ) : (
              <>
                <Typography.Text
                  style={{
                    fontSize: 12,
                    display: "block",
                  }}
                >
                  Storage (GB):
                </Typography.Text>
                <InputNumber
                  size="small"
                  value={featureConfig.storage}
                  onChange={(value) =>
                    handleFeatureChange(userIndex, feature, "storage", value)
                  }
                  placeholder="Enter storage in GB"
                  min={20}
                  max={10000}
                  style={{ width: "100%" }}
                />
              </>
            )}
          </div>
        ) : (
          <div>
            <Space
              direction="horizontal"
              size={8}
              align="center"
              style={{ height: 24 }}
            >
              {isHostRestricted ? (
                <Tooltip title="Feature updates are not available for default TagMango hosts">
                  <Switch
                    size="small"
                    checked={featureConfig.enabled}
                    disabled
                  />
                </Tooltip>
              ) : showTierWarning && featureConfig.enabled ? (
                <Switch
                  size="small"
                  checked={featureConfig.enabled}
                  onChange={() => {
                    Modal.confirm({
                      title: "Disable plan feature?",
                      icon: (
                        <ExclamationCircleFilled style={{ color: "#faad14" }} />
                      ),
                      content:
                        "This feature is part of the creator's active plan. Disabling it will immediately revoke access. Are you sure you want to proceed?",
                      okText: "Yes, disable",
                      cancelText: "Cancel",
                      okButtonProps: { danger: true },
                      centered: true,
                      onOk: () =>
                        handleFeatureChange(
                          userIndex,
                          feature,
                          "enabled",
                          false
                        ),
                    });
                  }}
                />
              ) : (
                <Switch
                  size="small"
                  checked={featureConfig.enabled}
                  onChange={(checked) =>
                    handleFeatureChange(userIndex, feature, "enabled", checked)
                  }
                />
              )}

              {featureConfig.enabled &&
                feature !== MANAGEABLE_FEATURES.SPARKS && (
                  <DatePicker
                    value={
                      featureConfig.expiryAt
                        ? moment(featureConfig.expiryAt)
                        : null
                    }
                    onChange={(date) =>
                      handleFeatureChange(
                        userIndex,
                        feature,
                        "expiryAt",
                        date?.endOf("day")?.toISOString() || null
                      )
                    }
                    disabledDate={(current) =>
                      current && current < moment().startOf("day")
                    }
                    style={{
                      width: 140,
                      height: 24,
                      ...(expiryMissing ? { borderColor: COLORS.ERROR } : {}),
                    }}
                    placeholder={
                      expiryRequired ? "Select expiry *" : "No expiry"
                    }
                    size="small"
                    disabled={isHostRestricted}
                    status={expiryMissing ? "error" : undefined}
                  />
                )}
            </Space>
            {expiryMissing && (
              <Typography.Text
                style={{
                  fontSize: 10,
                  color: COLORS.ERROR,
                  display: "block",
                  marginTop: 2,
                }}
              >
                Expiry date is required for this feature
              </Typography.Text>
            )}

            {renderExpiryCountdown(featureConfig.expiryAt)}
          </div>
        )}
      </Space>
    );
  };

  const hasInvalidEntries = data.some((user) => user.status === "invalid");
  const validUsers = data.filter((user) => user.status === "valid");

  const columns = [
    {
      title: "Status",
      key: "status",
      width: 100,
      fixed: "left",
      render: (_, record) => {
        const tag = (
          <Tag
            icon={
              record.status === "valid" ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )
            }
            color={record.status === "valid" ? "success" : "error"}
            style={{
              cursor: record.status === "valid" ? "default" : "pointer",
            }}
          >
            {record.status === "valid" ? "Valid" : "Invalid"}
          </Tag>
        );

        if (record.status === "invalid" && record.errors?.length > 0) {
          return (
            <Tooltip title={record.errors.join(", ")} color="red">
              {tag}
            </Tooltip>
          );
        }

        return tag;
      },
    },
    {
      title: "Sl. No.",
      key: "serialNumber",
      width: 80,
      render: (_, record, index) => (
        <Typography.Text strong style={{ fontSize: 14 }}>
          {numFormatter({
            amount: index + 1,
            hideCurrency: true,
            hideTooltip: true,
          })}
        </Typography.Text>
      ),
    },
    {
      title: "User Info",
      key: "userInfo",
      width: 280,
      render: (_, record) => (
        <Space direction="vertical" size={2} style={{ whiteSpace: "nowrap" }}>
          {record.name ? (
            <Typography.Text
              strong={record.status === "valid"}
              style={{
                color:
                  record.status === "invalid"
                    ? COLORS.ERROR
                    : COLORS.TEXT_TITLE,
                fontSize: 14,
              }}
            >
              {record.name}
            </Typography.Text>
          ) : null}

          <Typography.Text
            style={{
              fontSize: 12,
              color:
                record.status === "invalid"
                  ? COLORS.ERROR
                  : COLORS.TEXT_SUBTITLE,
            }}
          >
            {record.email || (
              <span style={{ color: COLORS.ERROR }}>Missing Email</span>
            )}
          </Typography.Text>

          <Typography.Text
            style={{
              fontSize: 12,
              color:
                record.status === "invalid"
                  ? COLORS.ERROR
                  : COLORS.TEXT_SUBTITLE,
            }}
          >
            <strong style={{ fontWeight: 500, color: COLORS.TEXT_BODY }}>
              Host:
            </strong>{" "}
            {record.host || (
              <span style={{ color: COLORS.ERROR }}>Missing</span>
            )}
          </Typography.Text>
        </Space>
      ),
    },
  ];

  if (hasInvalidEntries) {
    columns.push({
      title: "Error",
      key: "error",
      width: 300,
      ellipsis: true,
      render: (_, record) =>
        record.status === "invalid" && record.errors?.length > 0 ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {record.errors.join("; ")}
          </Typography.Text>
        ) : (
          "-"
        ),
    });
  }

  if (isPreloaded) {
    columns.push(
      {
        title: "User Tier",
        dataIndex: "userTier",
        key: "userTier",
        width: 120,
        render: (userTier) => {
          const tierOption = USER_TIER_OPTIONS.find(
            (option) => option.value === userTier
          );
          return tierOption ? (
            <Tag color={TIER_COLOR_MAP[userTier]}>{tierOption.label}</Tag>
          ) : (
            <Typography.Text type="secondary" strong style={{ fontSize: 12 }}>
              NOT SET
            </Typography.Text>
          );
        },
      },
      {
        title: "Plan Type",
        dataIndex: "whitelabelPlanType",
        key: "planType",
        width: 150,
        render: (planType) => {
          const planOption = PLAN_TYPE_OPTIONS.find(
            (option) => option.value === planType
          );
          return planOption ? (
            <Tag color={WL_PLAN_COLOR_MAP[planType]}>{planOption.label}</Tag>
          ) : (
            <Typography.Text type="secondary" strong style={{ fontSize: 12 }}>
              NOT SET
            </Typography.Text>
          );
        },
      }
    );
  }

  // Add feature columns for valid users only
  if (validUsers.length > 0) {
    COLUMN_FEATURES.forEach((feature) => {
      columns.push({
        title: (
          <Space direction="vertical" size={4}>
            <div>
              {getFeatureColumnLabel(feature)}
              <Tooltip
                title={
                  FEATURE_MODAL_TOOLTIP[feature] ||
                  `Configure ${getFeatureColumnLabel(feature)} for users`
                }
              >
                <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 12 }} />
              </Tooltip>
            </div>

            {!isSameForAll && validUsers.length > 1 && (
              <Checkbox
                checked={sameForAllByFeature[feature]}
                onChange={(e) =>
                  handleSameForAllFeature(feature, e.target.checked)
                }
              >
                <Typography.Text style={{ fontSize: 11 }}>
                  Same for all
                </Typography.Text>
              </Checkbox>
            )}
          </Space>
        ),
        dataIndex: feature,
        key: feature,
        width: getFeatureColumnWidth(
          feature,
          feature === MANAGEABLE_FEATURES.MANGO_AI ? 280 : 200
        ),
        render: (_, record, index) => {
          if (record.status === "invalid") {
            return <Typography.Text type="secondary">N/A</Typography.Text>;
          }

          return renderFeatureControl(record, feature, index);
        },
      });
    });

    columns.push({
      title: (
        <div style={{ whiteSpace: "nowrap" }}>
          AI Credits
          <Tooltip title="The creator's lifetime AI credit balance, and what is left of this month's allowance. Anything entered here is ADDED to the balance on save — leave it blank for no top-up.">
            <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 12 }} />
          </Tooltip>
        </div>
      ),
      key: "aiCredits",
      width: 200,
      render: (_, record) => {
        if (record.status === "invalid") {
          return <Typography.Text type="secondary">N/A</Typography.Text>;
        }

        // keyed off the valid-users list, which is what handleFeatureChange
        // indexes into — the table index counts invalid rows too
        const userIndex = validUsers.findIndex((u) => u.key === record.key);
        if (userIndex === -1) return null;

        return renderMangoAiCredits(record, userIndex);
      },
    });

    columns.push({
      title: (
        <div>
          Withdrawal Delay (hrs)
          <Tooltip title="Hours an auto-approved standard withdrawal waits before payout. Only editable while Instant Withdrawal is active (enabled and unexpired) for the host. Leave blank to keep the host's current value (tier default if never set). 0 = no delay.">
            <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 12 }} />
          </Tooltip>
        </div>
      ),
      key: "standardWithdrawalDelay",
      width: 200,
      render: (_, record) => {
        if (record.status === "invalid") {
          return <Typography.Text type="secondary">N/A</Typography.Text>;
        }

        if (isDefaultHost(record.host)) {
          return (
            <Tooltip title="Withdrawal delay is not configurable for default TagMango hosts">
              <InputNumber size="small" disabled style={{ width: "100%" }} />
            </Tooltip>
          );
        }

        // the delay only applies to auto-approved payouts, so it stays
        // read-only until Instant Withdrawal is active for this host — an
        // expired grant counts as inactive
        if (!isInstantWithdrawalActive(record.featuresConfig)) {
          return (
            <Tooltip title="Withdrawal delay can only be set while Instant Withdrawal is active for this host">
              <InputNumber
                size="small"
                disabled
                value={record.standardWithdrawalDelay}
                placeholder="Tier default"
                style={{ width: "100%" }}
              />
            </Tooltip>
          );
        }

        return (
          <InputNumber
            size="small"
            min={0}
            precision={0}
            value={record.standardWithdrawalDelay}
            onChange={(value) => handleWithdrawalDelayChange(record.key, value)}
            placeholder="Tier default"
            style={{ width: "100%" }}
          />
        );
      },
    });
  }

  // antd only honours column widths literally when scroll.x is a number. With
  // `x: "auto"` the inner table renders at `width: auto; min-width: 100%`, so
  // table-layout:fixed sizes it to the container and shares the slack out
  // across the columns — every width becomes a proportional hint and changing
  // one barely moves it. Summing the columns pins each to its declared width
  // and lets the table scroll horizontally instead.
  const tableScrollX = columns.reduce(
    (total, column) => total + (column.width || 150),
    0
  );

  return (
    <div className="manage-features-modal-users-table">
      {/* Global Same for All Control */}
      {validUsers.length > 1 && (
        <Row
          style={{
            marginBottom: 16,
            // padding: "12px 16px",
            // background: COLORS.BACKGROUND,
            // borderRadius: 8,
            // border: `1px solid ${COLORS.BORDER}`,
          }}
        >
          <Col span={24}>
            <Checkbox
              checked={isSameForAll}
              onChange={(e) => onSameForAllChange(e.target.checked)}
            >
              <Typography.Text strong style={{ marginRight: 4 }}>
                Same configuration for all users
              </Typography.Text>
              <Tooltip title="When enabled, changes in the first user will apply to all users. Changes in other users will automatically disable this option for flexible customization.">
                <InfoCircleOutlined
                  style={{ marginLeft: 4, color: COLORS.ICON }}
                />
              </Tooltip>
            </Checkbox>
          </Col>
        </Row>
      )}

      {/* REMOVED: Bulk Configuration Row that was causing cross-column contamination
          The "Enable all" and "Expires on" controls were applying changes to ALL features
          instead of maintaining column-wise behavior. Each feature should be managed 
          independently through the individual column controls. */}

      {/* Unlimited Storage Alert */}
      {validUsers.some((user) =>
        ["tier2", "tier3", "tier4"].includes(user.userTier)
      ) && (
        <Alert
          message="For Pro, Advanced, and Ultimate plan users, changing storage limits will not affect their accounts as they have unlimited storage by default."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="table-container">
        <Table
          dataSource={data}
          columns={columns}
          pagination={false}
          scroll={{ x: tableScrollX }}
          rowKey={(record) => record.key}
          rowClassName={(record) =>
            record.status === "valid" ? "valid-row" : "invalid-row"
          }
          locale={{
            emptyText: "No users uploaded yet",
          }}
        />
      </div>

      {validUsers.length > 0 && (
        <Typography.Text
          type="secondary"
          style={{ marginTop: 8, display: "block", fontSize: 12 }}
        >
          * Only valid users will be processed. Invalid users will be ignored
          during save.
        </Typography.Text>
      )}
    </div>
  );
};

UsersTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      email: PropTypes.string,
      host: PropTypes.string,
      userTier: PropTypes.string,
      whitelabelPlanType: PropTypes.string,
      status: PropTypes.string,
      errors: PropTypes.arrayOf(PropTypes.string),
      featuresConfig: PropTypes.shape(),
      standardWithdrawalDelay: PropTypes.number,
      aiVerse: PropTypes.bool,
    })
  ).isRequired,
  onDataChange: PropTypes.func.isRequired,
  isSameForAll: PropTypes.bool.isRequired,
  onSameForAllChange: PropTypes.func.isRequired,
  isPreloaded: PropTypes.bool,
};

export default UsersTable;
